// Bulk Review Queue state store — survives tab / route navigation and follows the login.
//
// FinancialHubPage renders CorporateImportTab only while its tab is active, so
// every piece of React state inside the tab used to die the moment the owner
// looked at another tab (or another page). This module holds that state
// outside the component tree:
//
//   • an in-memory Map (the source of truth for the whole SPA session — parsed
//     sheet, decisions, cost checks, overrides, in-flight run counters), read
//     through useSyncExternalStore so a remounted tab renders the exact state
//     it left behind; setters keep writing after unmount, so an in-flight cost
//     check or Apply that finishes while another tab is open still lands;
//   • a server-side draft (D-099) for the keys in PERSISTED_KEYS: each key is
//     serialised (gzip + base64 when the browser has CompressionStream), sliced
//     and PUT to /corporate-import/draft/<key> under the signed-in Cognito user,
//     KMS-encrypted at rest in VitalCareCorporateImports. A reload, a tab the
//     browser put to sleep, a closed tab, a new tab — or ANOTHER COMPUTER — all
//     come back to the same sheet and every approval once the owner signs in.
//     Writes are debounced; every commit carries the revision the tab last saw,
//     so a stale tab / device gets a 409 instead of silently overwriting newer
//     work. The draft is read once, before the tab renders
//     (hydrateCorporateImportStore / useCorporateImportDraft). Nothing is kept
//     in IndexedDB / sessionStorage any more — the draft is not device-bound.
//
// HIPAA posture: the sheet rows live in browser memory while the tab is open and
// in the owner's own encrypted draft (per Cognito sub, owner/billing only) for
// at most DRAFT_TTL_MS; Discard deletes it server-side, sign-out / idle time-out
// drop the memory copy (the draft waits for the next sign-in on any device).

import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";
import {
  deleteCorporateImportDraft, deleteCorporateImportDraftKey, getCorporateImportDraft, getCorporateImportDraftKey, putCorporateImportDraftKey,
} from "../../services/api";

export const DRAFT_TTL_MS = 14 * 24 * 60 * 60 * 1000;
// Slice sizes stay under the Lambda's 290 000-byte part cap: base64 is 1 byte/char,
// raw JSON may be up to 3 bytes/char.
export const DRAFT_PART_CHARS = { "gzip-base64": 280000, json: 90000 };
export const DRAFT_BACKEND = "server";
const WRITE_DEBOUNCE_MS = 1500;

const IN_FLIGHT = new Set(["checking", "syncing", "sending", "saving", "running", "loading"]);
const idleIfInterrupted = (idle) => (v) => (v && IN_FLIGHT.has(v.status) ? idle : v);

// Keys saved to the server-side draft. `hydrate` repairs a value that came back
// after a full reload interrupted the async work it described — no run survives
// a reload, so an in-flight status would otherwise hang forever.
// A persisted toggle map that comes back as anything but a plain object (older draft, failed chunk decode) must not
// freeze the month accordion — it falls back to the defaults instead.
const plainObjectOrEmpty = (v) => (v && typeof v === "object" && !Array.isArray(v) ? v : {});
export const PERSISTED_KEYS = {
  parsed: {}, decisions: {}, filter: {}, applyLog: {}, edits: {}, fixSkips: {}, costChecks: {},
  quantityOverrides: {}, uomDrafts: {}, uomOverrides: {}, uomErrors: {}, uomDimensions: {},
  formulationPicks: {}, formulationOptions: {}, learnedAliases: {}, monthToggles: { hydrate: plainObjectOrEmpty }, monthShown: { hydrate: plainObjectOrEmpty },
  showProfitBreakdown: {}, profitSearch: {}, priceOverrides: {}, calFilter: {}, approveNote: {}, supplyMode: {}, approvedMonth: {}, approvedTherapyOpen: {},
  applying: { hydrate: () => false },
  archive: { hydrate: idleIfInterrupted({ status: "idle", error: "", report: null }) },
  // D-110: a write-back that became a server-side job survives the reload — the tab reconnects to
  // the job on mount; a pre-job "syncing" (the POST itself) cannot be resumed and reads as idle.
  calWriteback: { hydrate: (v) => (v && v.status === "syncing" && v.jobId ? v : idleIfInterrupted({ status: "idle", reconciled: 0, failed: 0, errors: [] })(v)) },
  costCheckState: { hydrate: (v) => (v && v.status === "checking" ? { ...v, status: "error", error: "cost check interrupted by a page reload — Retry" } : v) },
  calendarSync: { hydrate: (v) => (v && v.status === "syncing" ? { ...v, status: "error", note: "Calendar sync interrupted by a page reload — re-sync" } : v) },
  // D-097: a reload while a reconcile job is running keeps the jobId → the tab reconnects to the job.
  simStudio: { hydrate: (v) => (v && v.status === "sending" && v.jobId ? v : idleIfInterrupted({ status: "idle", error: "", json: "" })(v)) },
  simStudioPricing: { hydrate: idleIfInterrupted({ status: "idle", error: "", json: "", count: 0 }) },
  // D-113: autopilot ran once per sheet (keyed by parsedAt) — a reload never re-approves or re-sends.
  autopilot: {},
  // D-113: the finalize (Accept) job survives the reload like the write-back job; the POST itself cannot be resumed.
  finalize: { hydrate: (v) => (v && v.status === "finalizing" && v.jobId ? v : idleIfInterrupted({ status: "idle", error: "", jobId: "", report: null, pending: 0 })(v)) },
};

const mem = new Map();
// Keys that only hold their `initial` value (a reader touched them before hydrate finished):
// the account draft may still overwrite these; a key the user actually set is never overwritten.
const seeded = new Set();
const listeners = new Map();
const dirty = new Set();
let flushTimer = null;
let listening = false;
let writeChain = Promise.resolve();
// Bumped by clearCorporateImportStore (sign-out / idle / tests): an in-flight hydrate or
// write started under an older epoch must not touch memory or the server afterwards.
let epoch = 0;

// status: idle → loading → ready | unavailable (GET failed: the tab works, nothing is saved)
// sync:   idle | saving | saved | error (PUT failed — Retry) | conflict (409 — reload)
const draft = { status: "idle", sync: "idle", revision: 0, savedAt: null, error: "", promise: null };
let draftVersion = 0;
const draftListeners = new Set();
function touchDraft(patch) { Object.assign(draft, patch); draftVersion++; draftListeners.forEach((fn) => fn()); }

const errMsg = (e) => (e && e.status === 0 ? "network error" : (e && e.status ? `HTTP ${e.status}` : (e && e.message) || "unknown error"));

// ── encoding ──
function toBase64(bytes) {
  let s = "";
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  return btoa(s);
}
function fromBase64(s) {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
export async function encodeDraftValue(value) {
  const json = JSON.stringify(value === undefined ? null : value);
  if (typeof CompressionStream === "function" && typeof Blob === "function" && typeof Response === "function") {
    try {
      const stream = new Blob([json]).stream().pipeThrough(new CompressionStream("gzip"));
      return { encoding: "gzip-base64", data: toBase64(new Uint8Array(await new Response(stream).arrayBuffer())) };
    } catch (_) { /* fall through to plain JSON */ }
  }
  return { encoding: "json", data: json };
}
export async function decodeDraftValue({ encoding, data }) {
  if (encoding === "gzip-base64") {
    if (typeof DecompressionStream !== "function") throw new Error("this browser cannot read a compressed draft");
    const stream = new Blob([fromBase64(data)]).stream().pipeThrough(new DecompressionStream("gzip"));
    return JSON.parse(await new Response(stream).text());
  }
  return JSON.parse(data);
}
/** Slice without splitting a surrogate pair (DynamoDB needs valid UTF-8 per part). */
export function sliceDraftData(data, size) {
  const out = [];
  let i = 0;
  while (i < data.length) {
    let end = Math.min(data.length, i + size);
    const c = data.charCodeAt(end - 1);
    if (end < data.length && c >= 0xd800 && c <= 0xdbff) end -= 1;
    out.push(data.slice(i, end));
    i = end;
  }
  return out.length ? out : [data];
}
const newToken = () => "t" + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);

// ── writes ──
async function writeKeys(keys, myEpoch) {
  if (myEpoch !== epoch || draft.status !== "ready") return;
  touchDraft({ sync: "saving", error: "" });
  let key = null;
  try {
    for (key of keys) {
      if (myEpoch !== epoch) return;
      const value = mem.get(key);
      if (!mem.has(key) || value === undefined) {
        const r = await deleteCorporateImportDraftKey(key, draft.revision);
        if (myEpoch !== epoch) return;
        if (r && Number.isInteger(r.revision)) touchDraft({ revision: r.revision, savedAt: r.savedAt || draft.savedAt });
        continue;
      }
      const { encoding, data } = await encodeDraftValue(value);
      const parts = sliceDraftData(data, DRAFT_PART_CHARS[encoding]);
      const token = newToken();
      for (let i = 0; i < parts.length; i++) {
        if (myEpoch !== epoch) return;
        const r = await putCorporateImportDraftKey(key, { baseRevision: draft.revision, token, part: i, parts: parts.length, encoding, data: parts[i] });
        if (myEpoch !== epoch) return;
        if (r && r.committed) touchDraft({ revision: r.revision, savedAt: r.savedAt || null });
      }
    }
    if (myEpoch === epoch) touchDraft({ sync: "saved", error: "" });
  } catch (e) {
    if (myEpoch !== epoch) return;
    if (e && e.status === 409) {
      dirty.clear();
      touchDraft({ sync: "conflict", error: "this draft was changed on another device or tab — reload to continue" });
      return;
    }
    // keep the unsaved keys dirty: the next change or Retry sends them again
    keys.slice(keys.indexOf(key)).forEach((k) => dirty.add(k));
    touchDraft({ sync: "error", error: `draft save failed (${errMsg(e)})` });
  }
}

function flushStorage() {
  flushTimer = null;
  if (!dirty.size || draft.status !== "ready" || draft.sync === "conflict") return;
  const keys = [...dirty];
  dirty.clear();
  const myEpoch = epoch;
  writeChain = writeChain.then(() => writeKeys(keys, myEpoch)).catch(() => { /* reported through draft.sync */ });
}
function flushNow() { if (flushTimer) clearTimeout(flushTimer); flushStorage(); }

function scheduleFlush(key) {
  dirty.add(key);
  if (!flushTimer) flushTimer = setTimeout(flushStorage, WRITE_DEBOUNCE_MS);
  if (!listening && typeof window !== "undefined") {
    listening = true;
    // Start the pending writes before the page is hidden / unloaded (sleep, close, navigate away).
    window.addEventListener("pagehide", flushNow);
    document.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") flushNow(); });
  }
}

/** True while a change is still on its way to the server (or failed to get there). */
export function hasPendingDraftWrites() { return dirty.size > 0 || draft.sync === "saving" || (draft.sync === "error" && draft.status === "ready"); }

/** Retry a failed save (sync === "error"). A conflict needs a page reload. */
export function retryCorporateImportDraftSave() {
  if (draft.sync !== "error") return;
  touchDraft({ sync: "idle", error: "" });
  flushNow();
}

// ── hydrate ──
async function fetchDeferredKey(key, meta) {
  const parts = [];
  for (let i = 0; i < (meta.parts || 1); i++) {
    const r = await getCorporateImportDraftKey(key, i);
    parts.push(r.data);
  }
  return { encoding: meta.encoding, data: parts.join("") };
}

/**
 * Load the signed-in user's draft once, before the tab renders. Keys already in
 * memory win (the SPA copy is authoritative). A failed GET leaves the tab usable
 * but marks the draft unavailable — nothing is saved until a reload succeeds.
 */
export function hydrateCorporateImportStore() {
  if (draft.promise) return draft.promise;
  const myEpoch = epoch;
  touchDraft({ status: "loading", error: "" });
  draft.promise = (async () => {
    try {
      const r = await getCorporateImportDraft();
      if (myEpoch !== epoch) return draft.status;
      const savedAt = r && r.savedAt ? Date.parse(r.savedAt) : 0;
      const expired = !!savedAt && Date.now() - savedAt > DRAFT_TTL_MS;
      const entries = { ...((r && r.entries) || {}) };
      if (!expired) {
        for (const key of (r && r.deferred) || []) {
          if (!PERSISTED_KEYS[key] || !r.keys || !r.keys[key]) continue;
          try { entries[key] = await fetchDeferredKey(key, r.keys[key]); } catch (_) { /* that key is left out */ }
        }
        if (myEpoch !== epoch) return draft.status;
        for (const [key, spec] of Object.entries(PERSISTED_KEYS)) {
          const entry = entries[key];
          if (!entry || (mem.has(key) && !seeded.has(key))) continue;
          try {
            const raw = await decodeDraftValue(entry);
            mem.set(key, spec.hydrate ? spec.hydrate(raw) : raw);
            seeded.delete(key);
          } catch (_) { /* unreadable key is skipped — the rest of the draft still loads */ }
        }
      }
      if (myEpoch !== epoch) return draft.status;
      touchDraft({ status: "ready", revision: expired ? 0 : (r && r.revision) || 0, savedAt: expired ? null : (r && r.savedAt) || null, sync: !expired && r && r.revision ? "saved" : "idle", error: "" });
      if (expired) deleteCorporateImportDraft().catch(() => { /* best-effort; the next commit starts at revision 0 anyway */ });
    } catch (e) {
      if (myEpoch !== epoch) return draft.status;
      touchDraft({ status: "unavailable", error: `your saved draft could not be loaded (${errMsg(e)}) — work here is NOT being saved; reload to try again` });
    }
    [...mem.keys()].forEach(notify);
    return draft.status;
  })();
  return draft.promise;
}

/** Tests only: snapshot of the draft state. */
export function _draftStateForTest() { return { ...draft, promise: undefined }; }

/** Tests only: wait for every scheduled write to settle. */
export function _settleDraftWritesForTest() { flushNow(); return writeChain; }

/** Tests only: forget hydration + sync state (memory untouched). */
export function _resetDraftForTest() {
  epoch++;
  dirty.clear();
  if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
  writeChain = Promise.resolve();
  touchDraft({ status: "idle", sync: "idle", revision: 0, savedAt: null, error: "", promise: null });
}

/** Draft state for the gate / status badge. Kicks off the one-time hydrate. */
export function useCorporateImportDraft() {
  const subscribe = useCallback((fn) => { draftListeners.add(fn); return () => draftListeners.delete(fn); }, []);
  const read = useCallback(() => draftVersion, []);
  useSyncExternalStore(subscribe, read, read);
  useEffect(() => { if (draft.status === "idle") hydrateCorporateImportStore(); }, []);
  return { status: draft.status, ready: draft.status === "ready" || draft.status === "unavailable", backend: DRAFT_BACKEND, sync: draft.sync, revision: draft.revision, savedAt: draft.savedAt, error: draft.error };
}

function notify(key) {
  const set = listeners.get(key);
  if (set) set.forEach((fn) => fn());
}

const init = (initial) => (typeof initial === "function" ? initial() : initial);

/** Current value for `key`; seeds it from `initial` on first access (the draft is loaded by hydrate). */
export function getStored(key, initial) {
  if (mem.has(key)) return mem.get(key);
  const v = init(initial);
  mem.set(key, v);
  seeded.add(key);
  return v;
}

export function setStored(key, value) {
  const prev = mem.get(key);
  mem.set(key, value);
  seeded.delete(key);
  if (PERSISTED_KEYS[key]) scheduleFlush(key);
  if (prev !== value) notify(key);
}

/**
 * Drop every stored key in memory + module refs — sign-out / idle time-out / tests.
 * The server-side draft is left alone: it belongs to the account and comes back
 * on the next sign-in (Discard is the only thing that deletes it).
 */
export function clearCorporateImportStore() {
  const keys = [...mem.keys()];
  mem.clear();
  seeded.clear();
  refs.forEach((r) => { r.current = r.reset(); });
  _resetDraftForTest();
  keys.forEach(notify);
}

/**
 * Discard button: the component has already reset every stored key to its
 * initial value; drop those pending writes and delete the account draft instead.
 */
export function discardCorporateImportDraft() {
  dirty.clear();
  if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
  const myEpoch = epoch;
  touchDraft({ sync: "idle", revision: 0, savedAt: null, error: "" });
  writeChain = writeChain
    .then(() => deleteCorporateImportDraft())
    .catch((e) => { if (myEpoch === epoch) touchDraft({ sync: "error", error: `draft discard did not reach the server (${errMsg(e)}) — the old sheet may reappear on your next sign-in` }); });
  return writeChain;
}

/** Tests only: forget the in-memory copy but keep the account draft — simulates a page reload. */
export async function _dropMemoryForTest() {
  await _settleDraftWritesForTest();
  const keys = [...mem.keys()];
  mem.clear();
  seeded.clear();
  refs.forEach((r) => { r.current = r.reset(); });
  touchDraft({ status: "idle", sync: "idle", promise: null });
  keys.forEach(notify);
}

/** Flush pending draft writes now (tests / pagehide). */
export function flushCorporateImportStore() { flushNow(); }

/**
 * useState replacement backed by the store. Same tuple + functional-update
 * semantics; the value outlives the component.
 */
export function useStoredState(key, initial) {
  const initialRef = useRef(initial);
  const subscribe = useCallback((fn) => {
    if (!listeners.has(key)) listeners.set(key, new Set());
    listeners.get(key).add(fn);
    return () => { const s = listeners.get(key); if (s) { s.delete(fn); if (!s.size) listeners.delete(key); } };
  }, [key]);
  const read = useCallback(() => getStored(key, initialRef.current), [key]);
  const value = useSyncExternalStore(subscribe, read, read);
  const set = useCallback((next) => {
    const current = getStored(key, initialRef.current);
    setStored(key, typeof next === "function" ? next(current) : next);
  }, [key]);
  return [value, set];
}

/**
 * Module-scope setter for a stored key — same functional-update semantics as a
 * useState setter, stable identity, usable from anywhere (including after the
 * tab unmounted, so an in-flight cost check or Apply still lands).
 */
export function storedSetter(key) {
  return (next) => {
    const current = getStored(key, undefined);
    setStored(key, typeof next === "function" ? next(current) : next);
  };
}

const refs = new Map();
/**
 * Module-scope `{ current }` box for race guards / caches that must outlive the
 * component (run counters, pricing cache). Reset by clearCorporateImportStore().
 */
export function moduleRef(key, initial) {
  if (!refs.has(key)) refs.set(key, { current: init(initial), reset: () => init(initial) });
  return refs.get(key);
}
